import { useEffect, useMemo, useState } from 'react';
import { BezierCurveEditor } from 'react-bezier-curve-editor';
import 'react-bezier-curve-editor/index.css';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import useCorrectionStore from '@correction/hooks/useCorrectionStore.ts';
import {
  DEFAULT_TREATMENT_CONSTRAINTS,
  cloneConstraintsMap,
  DEFAULT_BEZIER_POINTS,
  type ToothConstraintValues,
} from '@correction/config/treatmentConstraints';
import { lowerFDI, upperFDI, type FDI_Notation } from '@/common/FDI_Notation';
import { TreatmentConstraintsTable } from './TreatmentConstraintsTable';
import './TreatmentConstraintsEditor.css';

type ConstraintGroup = 'translationLimit' | 'rotationLimitDeg';
type ConstraintAxis = 'x' | 'y' | 'z';

const FDI_ORDER: FDI_Notation[] = [...upperFDI, ...lowerFDI];

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

const cubicBezier = (t: number, p0: number, p1: number, p2: number, p3: number) => {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
};

const cubicBezierDerivative = (t: number, p0: number, p1: number, p2: number, p3: number) => {
  const u = 1 - t;
  return 3 * u * u * (p1 - p0) + 6 * u * t * (p2 - p1) + 3 * t * t * (p3 - p2);
};

const evaluateBezier = (progress: number, [p1x, p1y, p2x, p2y]: [number, number, number, number]) => {
  let param = clamp01(progress);
  for (let i = 0; i < 5; i++) {
    const xEstimate = cubicBezier(param, 0, p1x, p2x, 1);
    const error = xEstimate - progress;
    if (Math.abs(error) < 1e-5) break;
    const derivative = cubicBezierDerivative(param, 0, p1x, p2x, 1);
    if (Math.abs(derivative) < 1e-6) break;
    param = clamp01(param - error / derivative);
  }
  return cubicBezier(param, 0, p1y, p2y, 1);
};

const computeWeightsFromBezier = (
  controlPoints: [number, number, number, number],
  cyclesCount: number,
) => {
  const count = Math.max(1, cyclesCount);
  const weights: number[] = [];
  let previous = 0;
  for (let step = 1; step <= count; step++) {
    const progress = step / count;
    const current = evaluateBezier(progress, controlPoints);
    const delta = Math.max(0, current - previous);
    weights.push(delta);
    previous = current;
  }
  const sum = weights.reduce((acc, value) => acc + value, 0);
  if (sum <= 1e-6) {
    return Array(count).fill(1 / count);
  }
  return weights.map((value) => value / sum);
};

type TreatmentConstraintsProps = {
  className?: string;
};

export function TreatmentConstraints({ className }: TreatmentConstraintsProps) {
  const { jaws, updateStore } = useCorrectionStore();

  const upperTransform = jaws?.upperJaw?.getTransformIterations();
  const lowerTransform = jaws?.lowerJaw?.getTransformIterations();

  const [constraints, setConstraints] = useState(() => cloneConstraintsMap(DEFAULT_TREATMENT_CONSTRAINTS));
  const [curvePoints, setCurvePoints] = useState<[number, number, number, number]>([...DEFAULT_BEZIER_POINTS]);
  const [isTableOpen, setIsTableOpen] = useState(false);

  useEffect(() => {
    if (!upperTransform) return;
    setConstraints(cloneConstraintsMap(upperTransform.getConstraints()));
    setCurvePoints(upperTransform.getBezierControlPoints());
  }, [upperTransform]);

  const totalCycles = useMemo(() => {
    if (!jaws) return 0;
    return Math.max(
      jaws.lowerJaw.getTransformIterations().getIterationsCount(),
      jaws.upperJaw.getTransformIterations().getIterationsCount(),
    );
  }, [jaws?.lowerJaw, jaws?.upperJaw, constraints, curvePoints]);

  if (!jaws || !upperTransform || !lowerTransform) {
    return null;
  }

  const displayedCycles = Math.max(totalCycles, 1);

  const rows = useMemo(
    () =>
      FDI_ORDER.map((tooth) => {
        const constraint = constraints.get(tooth);
        return constraint ? { tooth, constraint } : null;
      }).filter((row): row is { tooth: FDI_Notation; constraint: ToothConstraintValues } => row !== null),
    [constraints],
  );

  const applyConstraints = (nextConstraints: Map<FDI_Notation, ToothConstraintValues>) => {
    const cloned = cloneConstraintsMap(nextConstraints);
    setConstraints(cloned);
    upperTransform.setConstraints(cloned);
    lowerTransform.setConstraints(cloned);
    recalculateAllIterations();
  };

  const recalculateAllIterations = () => {
    jaws.upperJaw.teeth.forEach((tooth) => {
      upperTransform.calculateIterations(
        tooth.getFDI(),
        tooth.getMovedPosition(),
        tooth.getMovedRotQuaternion(),
        tooth.getOrthoFrame(),
      );
    });
    jaws.lowerJaw.teeth.forEach((tooth) => {
      lowerTransform.calculateIterations(
        tooth.getFDI(),
        tooth.getMovedPosition(),
        tooth.getMovedRotQuaternion(),
        tooth.getOrthoFrame(),
      );
    });
    updateStore();
  };

  const handleConstraintChange = (
    tooth: FDI_Notation,
    group: ConstraintGroup,
    axis: ConstraintAxis,
    value: number,
  ) => {
    const current = constraints.get(tooth);
    if (!current) return;

    const updatedMap = cloneConstraintsMap(constraints);
    const target = updatedMap.get(tooth);
    if (!target) return;

    target[group][axis] = value;
    applyConstraints(updatedMap);
  };

  const resetConstraints = () => {
    applyConstraints(DEFAULT_TREATMENT_CONSTRAINTS);
  };

  const handleBezierChange = (points: number[]) => {
    if (!Array.isArray(points)) return;

    const rawPoints = points.length === 4 ? points : points.slice(2, 6);
    if (rawPoints.length !== 4) return;

    const tuple = rawPoints.map((value) => clamp01(value)) as [number, number, number, number];
    setCurvePoints(tuple);
    upperTransform.setBezierControlPoints(tuple);
    lowerTransform.setBezierControlPoints(tuple);
    recalculateAllIterations();
  };

  const resetCurve = () => {
    handleBezierChange([...DEFAULT_BEZIER_POINTS]);
  };

  const computedWeights = useMemo(
    () => computeWeightsFromBezier(curvePoints, Math.max(totalCycles, 1)),
    [curvePoints, totalCycles],
  );

  return (
    <section
      className={cn(
        'relative rounded-2xl border border-white/8 bg-content/95 p-4 text-white shadow-lg shadow-black/40 backdrop-blur',
        className,
      )}
    >
      <header className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45">Treatment constraints</p>
            <h2 className="text-base font-semibold leading-tight text-white">Movement limits</h2>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost-light"
              size="sm"
              type="button"
              onClick={() => setIsTableOpen((prev) => !prev)}
            >
              {isTableOpen ? 'Hide limits' : 'Edit limits'}
            </Button>
            <Button variant="ghost-light" size="sm" type="button" onClick={resetConstraints}>
              Reset all
            </Button>
          </div>
        </div>
        <p className="text-xs text-white/55">
          Adjust the maximum movement allowed per tooth. Changes apply instantly to both jaws.
        </p>
      </header>

      <div className="mt-4 space-y-2.5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45">Progression curve</p>
            <p className="text-xs text-white/55">
              Shape how movement accumulates across cycles. Raising the curve earlier allocates more movement to early cycles.
            </p>
          </div>
          <Button variant="ghost-light" size="sm" type="button" onClick={resetCurve}>
            Reset curve
          </Button>
        </div>

        <div className="relative w-full max-w-[420px] rounded-xl border border-white/8 bg-content-dark/70 p-3">
          <div className="pointer-events-none absolute inset-4 flex">
            {Array.from({ length: displayedCycles + 1 }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  'h-full border-l border-dashed border-primary/20',
                  index === 0 ? 'opacity-60' : 'opacity-25',
                )}
                style={{ width: `${100 / displayedCycles}%` }}
              />
            ))}
          </div>
          <BezierCurveEditor
            className="treatment-curve-editor"
            value={curvePoints}
            onChange={handleBezierChange}
            size={320}
            outerAreaSize={36}
            strokeWidth={2}
            allowNodeEditing={false}
            outerAreaColor="rgba(15,23,42,0.6)"
            innerAreaColor="rgba(15,23,42,0.92)"
            rowColor="rgba(30,41,59,0.65)"
            curveLineColor="#38bdf8"
            handleLineColor="rgba(94,234,212,0.6)"
            startHandleColor="#22d3ee"
            endHandleColor="#f97316"
            fixedHandleColor="#1f2937"
            startHandleClassName="treatment-curve-editor__handle-start"
            startHandleActiveClassName="treatment-curve-editor__handle-active"
            endHandleClassName="treatment-curve-editor__handle-end"
            endHandleActiveClassName="treatment-curve-editor__handle-active"
            fixedPointActiveClassName="treatment-curve-editor__handle-active"
          />
          <div className="treatment-curve-editor__grid-overlay" />
        </div>

        <div className="flex flex-wrap gap-1.5 text-[10px] text-white/65">
          {computedWeights.slice(0, 12).map((weight, index) => (
            <span
              key={index}
              className="rounded-md border border-white/8 bg-content-dark/60 px-1.5 py-0.5 tabular-nums"
            >
              <span className="text-white/40">C{index + 1}</span> {(weight * 100).toFixed(1)}%
            </span>
          ))}
          {computedWeights.length > 12 && (
            <span className="rounded-md border border-dashed border-white/8 bg-content-dark/40 px-1.5 py-0.5 text-white/45">
              +{computedWeights.length - 12} more
            </span>
          )}
        </div>
      </div>

      {isTableOpen && (
        <TreatmentConstraintsTable
          rows={rows}
          onChange={handleConstraintChange}
          onReset={resetConstraints}
          onClose={() => setIsTableOpen(false)}
        />
      )}
    </section>
  );
}
